import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { FocalArea } from './models/focal-area.model';
import { DEFAULT_FOCAL_AREA } from './models/focal-area.constants';
import { CropperPosition, ImageCroppedEvent } from '../../components/image-cropper/interfaces';
import { ProjectsQuery } from '../../../_core/state/project';
import { BusinessQuery } from '../../../_core/state/business';
import { GlobalService } from '../../../_core/state/global/global.service';
import { DetectionResult } from './models/detection-result.interface';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../components/confirm-dialog/confirm-dialog.component';
import { CreativeUnitDimension } from '../image-editor-dialog.component';
import { LayerOptimizationsDto, ResizeMode } from '../../../../../../api/src/creative-unit-package/models/layer/layer-optimization.dto';
import { LayerType } from '../../../../../../api/src/creative-unit-package/models/layer/layer.dto';
import { FocalAreaUtils } from './models/focal-area.utils';

@Component({
	selector: 'app-focal-area-cropping',
	templateUrl: './focal-area-cropping.component.html',
	styleUrls: ['./focal-area-cropping.component.scss']
})
export class FocalAreaCroppingComponent implements OnInit, OnChanges, OnDestroy {
	@Input() asset: any = null;
	@Input() imageUrl: string | null = null;
	@Input() assetId?: string;
	@Input() selectMode: boolean = false;
	@Input() initialTargetCrop?: { x: number; y: number; width: number; height: number };
	@Input() creativeUnits?: CreativeUnitDimension[];
	@Output() assetUpdated = new EventEmitter<any>();
	@Output() focalAreaSelected = new EventEmitter<{ focalArea: FocalArea; imageNaturalWidth: number; imageNaturalHeight: number }>();

	focalAreas: FocalArea[] = [];
	selectedFocalAreaIndex: number | null = null;
	visibleFocalAreaIndexes: number[] = [];
	displayedSelectedIndex: number | null = null;
	displayedEditingIndex: number | null = null;
	selectedTagFilters: string[] = [];
	previewMode: string = 'original';
	customAspectRatio: string = '16:9';
	currentCropperPosition: CropperPosition | null = null;
	imageNaturalWidth: number = 0;
	imageNaturalHeight: number = 0;
	isSaving: boolean = false;
	saveError: string | null = null;
	isLoadingImage: boolean = false;
	loadImageError: string | null = null;
	previewFocalArea: FocalArea | null = null; // For AI suggestion previews
	aspectRatioDirtyFlags: Map<number, boolean> = new Map(); // Track manual aspect ratio edits per focal area
	editingNameIndex: number | null = null; // Track which focal area name is being edited
	assetExistsInDb: boolean = false;
	dirtyFocalAreaIndices: Set<number> = new Set();
	hasUnsavedChanges: boolean = false;
	private originalFocalAreas: FocalArea[] = [];
	private isLoadingFocalAreaIntoCropper: boolean = false; // Flag to prevent duplicate creation
	private snapshotBeforeOperation: FocalArea[] | null = null; // For undo functionality

	// Auto-detection properties
	autoDetectMode: boolean = false;
	detectionResult: DetectionResult | null = null;
	isLoadingDetection: boolean = false;

	private focalAreas$ = new BehaviorSubject<FocalArea[]>([]);
	private selectedIndex$ = new BehaviorSubject<number | null>(null);
	private destroy$ = new Subject<void>();

	get visibleFocalAreas(): FocalArea[] {
		return this.visibleFocalAreaIndexes
			.map(index => this.focalAreas[index])
			.filter((area): area is FocalArea => !!area);
	}

	constructor(
		private http: HttpClient,
		private projectsQuery: ProjectsQuery,
		private businessQuery: BusinessQuery,
		private globalService: GlobalService,
		private cdr: ChangeDetectorRef,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {

		// Subscribe to observables if needed
	this.focalAreas$.pipe(takeUntil(this.destroy$)).subscribe(areas => {
		this.focalAreas = areas;
		this.rebuildVisibleFocalAreas();
	});

	this.selectedIndex$.pipe(takeUntil(this.destroy$)).subscribe(index => {
		this.selectedFocalAreaIndex = index;
		this.syncDisplayIndexes();
	});

		// Initialize from asset if provided
		void this.initializeFromAsset();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['asset'] && !changes['asset'].firstChange) {
			void this.initializeFromAsset();
		}
	}

	private async initializeFromAsset(): Promise<void> {
		const assetPath = this.asset?.assetPath || this.imageUrl;
		const businessId = this.coerceId(this.asset?.businessId || this.getActiveBusinessId());

		if (!assetPath) {
			this.assetExistsInDb = false;
			this.setFocalAreasFromAsset(null);
			return;
		}

		const payload: Record<string, any> = {
			assetPath
		};

		// if (businessId && this.isUuid(businessId)) {
		// 	payload.businessId = businessId;
		// }

		try {
			const response = await this.http
				.post<any>(`${environment.apiUrl}/asset/find`, payload)
				.toPromise();

			const fetchedAsset = response?.data?.[0];

			if (fetchedAsset) {
				this.applyAssetData(fetchedAsset, true);
			} else if (this.asset) {
				this.applyAssetData(this.asset, !!this.asset.id);
			} else {
				this.assetExistsInDb = false;
				this.setFocalAreasFromAsset(null);
			}
		} catch (error) {
			console.error('Failed to fetch latest asset metadata:', error);
			if (this.asset) {
				this.applyAssetData(this.asset, !!this.asset.id);
			} else {
				this.assetExistsInDb = false;
				this.setFocalAreasFromAsset(null);
			}
		}

		// If initialTargetCrop is provided, match or create a focal area
		if (this.initialTargetCrop) {
			this.matchOrCreateFocalArea();
		}
	}

	private applyAssetData(assetData: any, existsInDb: boolean): void {
		if (!assetData) {
			this.assetExistsInDb = false;
			this.setFocalAreasFromAsset(null, true);
			return;
		}

		this.asset = assetData;
		this.assetId = assetData?.id;
		this.imageUrl = assetData?.assetPath || assetData?.localUrl || this.imageUrl;
		this.assetExistsInDb = existsInDb;
		this.setFocalAreasFromAsset(assetData, true);
	}

	private setFocalAreasFromAsset(assetData: any, resetSnapshot: boolean = true): void {
		const targetCrops = assetData?.metadata?.targetCrops;
		if (targetCrops && Array.isArray(targetCrops)) {
			this.focalAreas = this.cloneFocalAreas(targetCrops as FocalArea[]);
		} else {
			this.focalAreas = [];
		}
		if (resetSnapshot) {
			this.selectedTagFilters = [];
		}
		this.focalAreas$.next([...this.focalAreas]);
		this.aspectRatioDirtyFlags.clear();
		if (resetSnapshot) {
			this.setOriginalSnapshotFromCurrent();
		} else {
			this.updateDirtyState();
		}

		// Auto-select first focal area if none selected
		this.rebuildVisibleFocalAreas();
		if (this.visibleFocalAreaIndexes.length > 0 && this.selectedFocalAreaIndex === null) {
			this.onSelectFocalArea(0);
		}
	}

	private cloneFocalAreas(source: FocalArea[]): FocalArea[] {
		return (source || []).map(area => ({
			...area,
			name: area?.name || DEFAULT_FOCAL_AREA.name,
			tags: Array.isArray(area?.tags) ? [...area.tags] : [],
			boundingBox: area?.boundingBox ? { ...area.boundingBox } : { x: 0, y: 0, width: 0, height: 0 }
		}));
	}

	private rebuildVisibleFocalAreas(): void {
		const filters = this.selectedTagFilters.map(tag => tag.toLowerCase());
		const matches: number[] = [];
		this.focalAreas.forEach((area, index) => {
			if (filters.length === 0) {
				matches.push(index);
				return;
			}

			const hasMatch = (area.tags || []).some(tag => filters.includes(tag.toLowerCase()));
			if (hasMatch) {
				matches.push(index);
			}
		});
		this.visibleFocalAreaIndexes = matches;
		this.syncDisplayIndexes();
	}

	private resolveActualIndex(displayIndex: number): number | null {
		if (displayIndex < 0 || displayIndex >= this.visibleFocalAreaIndexes.length) {
			return null;
		}
		return this.visibleFocalAreaIndexes[displayIndex];
	}

	private resolveDisplayIndex(actualIndex: number | null): number | null {
		if (actualIndex === null) {
			return null;
		}
		const displayIndex = this.visibleFocalAreaIndexes.indexOf(actualIndex);
		return displayIndex >= 0 ? displayIndex : null;
	}

	private syncDisplayIndexes(): void {
		this.displayedSelectedIndex = this.resolveDisplayIndex(this.selectedFocalAreaIndex);
		this.displayedEditingIndex = this.resolveDisplayIndex(this.editingNameIndex);
	}

	private setOriginalSnapshotFromCurrent(): void {
		this.originalFocalAreas = this.cloneFocalAreas(this.focalAreas);
		this.dirtyFocalAreaIndices.clear();
		this.hasUnsavedChanges = false;
	}

	private normalizeFocalArea(area: FocalArea): any {
		const round = (value: number | undefined) =>
			value === undefined || value === null ? null : parseFloat(value.toFixed(4));

		return {
			name: area?.name || '',
			subjectType: area?.subjectType || '',
			areaType: area?.areaType || '',
			cropMode: area?.cropMode || 'contain',
			aspectRatio: area?.aspectRatio || '',
			targetWidth: area?.targetWidth ?? null,
			targetHeight: area?.targetHeight ?? null,
			isDefault: !!area?.isDefault,
			tags: Array.isArray(area?.tags) ? [...area.tags].sort() : [],
			boundingBox: {
				x: round(area?.boundingBox?.x),
				y: round(area?.boundingBox?.y),
				width: round(area?.boundingBox?.width),
				height: round(area?.boundingBox?.height)
			}
		};
	}

	private updateDirtyState(): void {
		this.dirtyFocalAreaIndices.clear();

		const current = this.focalAreas.map(area => this.normalizeFocalArea(area));
		const original = this.originalFocalAreas.map(area => this.normalizeFocalArea(area));
		const maxLength = Math.max(current.length, original.length);

		for (let i = 0; i < maxLength; i++) {
			const curr = current[i];
			const orig = original[i];

			if (!curr && !orig) {
				continue;
			}

			if (!curr || !orig) {
				if (curr) {
					this.dirtyFocalAreaIndices.add(i);
				}
				continue;
			}

			if (JSON.stringify(curr) !== JSON.stringify(orig)) {
				this.dirtyFocalAreaIndices.add(i);
			}
		}

		this.hasUnsavedChanges = this.dirtyFocalAreaIndices.size > 0 || current.length !== original.length;
	}

	isFocalAreaDirty(index: number): boolean {
		const actualIndex = this.resolveActualIndex(index);
		return actualIndex !== null ? this.dirtyFocalAreaIndices.has(actualIndex) : false;
	}

	/**
	 * Match the initialTargetCrop against existing focal areas or create a new one
	 * Converts float values (0-1) to percentage values (0-100)
	 * Only creates a new "Current Focal Area" when in selectMode (manual mode)
	 */
	private matchOrCreateFocalArea(): void {
		if (!this.initialTargetCrop) {
			return;
		}


		// Convert float values to percentages
		const targetX = this.initialTargetCrop.x * 100;
		const targetY = this.initialTargetCrop.y * 100;
		const targetWidth = this.initialTargetCrop.width * 100;
		const targetHeight = this.initialTargetCrop.height * 100;


		// Try to find a matching focal area (with small tolerance for floating point errors)
		const tolerance = 0.1; // 0.1% tolerance
		const matchIndex = this.focalAreas.findIndex(fa => {
			const match = Math.abs(fa.boundingBox.x - targetX) < tolerance &&
				   Math.abs(fa.boundingBox.y - targetY) < tolerance &&
				   Math.abs(fa.boundingBox.width - targetWidth) < tolerance &&
				   Math.abs(fa.boundingBox.height - targetHeight) < tolerance;
			return match;
		});


		if (matchIndex !== -1) {
			// Match found - auto-select it
			this.selectedFocalAreaIndex = matchIndex;
			this.selectedIndex$.next(matchIndex);

			// Load the matched focal area into the cropper
			if (this.imageNaturalWidth && this.imageNaturalHeight) {
				this.loadFocalAreaIntoCropper(this.focalAreas[matchIndex]);
			}
		} else if (this.selectMode) {
			// Only create a new "Current Focal Area" when in manual mode (selectMode = true)
			// In auto mode, we don't create a new focal area from the manual crop
			const aspectRatio = FocalAreaUtils.calculateAspectRatio(targetWidth, targetHeight);
			const newArea: FocalArea = {
				...DEFAULT_FOCAL_AREA,
				name: 'Current Focal Area',
				tags: [],
				aspectPriority: 'crop', // New focal areas use 'crop' priority
				boundingBox: {
					x: targetX,
					y: targetY,
					width: targetWidth,
					height: targetHeight
				},
				aspectRatio: aspectRatio,
				isDefault: false
			};


			this.focalAreas = [...this.focalAreas, newArea];
			this.focalAreas$.next([...this.focalAreas]);
			this.updateDirtyState();
			this.selectedFocalAreaIndex = this.focalAreas.length - 1;
			this.selectedIndex$.next(this.selectedFocalAreaIndex);


			// Load it into the cropper
			if (this.imageNaturalWidth && this.imageNaturalHeight) {
				this.loadFocalAreaIntoCropper(newArea);
			} else {
			}
		} else {
			// In auto mode, if no match found, just don't do anything
		}
	}

	get selectedFocalArea(): FocalArea | null {
		// If we're previewing an AI suggestion, return that instead
		if (this.previewFocalArea) {
			return this.previewFocalArea;
		}
		if (this.selectedFocalAreaIndex === null) return null;
		return this.focalAreas[this.selectedFocalAreaIndex] || null;
	}

	isAspectRatioDirty(index: number): boolean {
		const actualIndex = this.resolveActualIndex(index);
		if (actualIndex === null) {
			return false;
		}
		return this.aspectRatioDirtyFlags.get(actualIndex) || false;
	}

	onResetAspectRatio(index: number): void {
		// Clear the dirty flag
		const actualIndex = this.resolveActualIndex(index);
		if (actualIndex === null) {
			return;
		}
		this.aspectRatioDirtyFlags.delete(actualIndex);

		// Recalculate aspect ratio from current bounding box
		const focalArea = this.focalAreas[actualIndex];
		if (focalArea) {
			const aspectRatio = FocalAreaUtils.calculateAspectRatio(
				focalArea.boundingBox.width,
				focalArea.boundingBox.height
			);

			this.onUpdateFocalArea({
				index,
				focalArea: { aspectRatio },
				isManualAspectRatioEdit: false
			});
		}
	}

	onAddFocalArea(): void {
		const newArea: FocalArea = {
			...DEFAULT_FOCAL_AREA,
			name: `Focal Area ${this.focalAreas.length + 1}`,
			isDefault: this.focalAreas.length === 0, // First focal area is default
			tags: []
		};

		this.focalAreas = [...this.focalAreas, newArea];
		this.focalAreas$.next([...this.focalAreas]);
		this.updateDirtyState();
		this.selectedFocalAreaIndex = this.focalAreas.length - 1;
		this.selectedIndex$.next(this.selectedFocalAreaIndex);

		// Load the new focal area into the cropper
		this.loadFocalAreaIntoCropper(newArea);

		// Auto-start editing the name for new manual focal areas
		this.editingNameIndex = this.selectedFocalAreaIndex;
	}

	onSelectFocalArea(index: number): void {
		// Clear any preview when selecting a real focal area
		this.previewFocalArea = null;
		const actualIndex = this.resolveActualIndex(index);
		if (actualIndex === null) {
			return;
		}

		this.selectedFocalAreaIndex = actualIndex;
		this.selectedIndex$.next(actualIndex);
		this.syncDisplayIndexes();

		// Load the selected focal area's bounding box into the cropper
		if (this.focalAreas[actualIndex]) {
			const focalArea = this.focalAreas[actualIndex];
			this.loadFocalAreaIntoCropper(focalArea);
		}
	}

	onSelectFocalAreaForTarget(index: number): void {
		const actualIndex = this.resolveActualIndex(index);
		if (actualIndex === null || !this.focalAreas[actualIndex]) {
			return;
		}

		const focalArea = this.focalAreas[actualIndex];

		// Emit the selected focal area along with image dimensions
		this.focalAreaSelected.emit({
			focalArea,
			imageNaturalWidth: this.imageNaturalWidth,
			imageNaturalHeight: this.imageNaturalHeight
		});
	}

	private loadFocalAreaIntoCropper(focalArea: FocalArea): void {
		if (!this.imageNaturalWidth || !this.imageNaturalHeight) {
			// Image dimensions not yet available
			return;
		}

		// Set flag to prevent onImageCropped from creating duplicates during load
		this.isLoadingFocalAreaIntoCropper = true;

		// Convert percentage-based bounding box to pixel coordinates
		const x1 = (focalArea.boundingBox.x / 100) * this.imageNaturalWidth;
		const y1 = (focalArea.boundingBox.y / 100) * this.imageNaturalHeight;
		const x2 = x1 + (focalArea.boundingBox.width / 100) * this.imageNaturalWidth;
		const y2 = y1 + (focalArea.boundingBox.height / 100) * this.imageNaturalHeight;

		this.currentCropperPosition = { x1, y1, x2, y2 };

		// Clear the flag after a short delay to allow cropper to initialize
		setTimeout(() => {
			this.isLoadingFocalAreaIntoCropper = false;
		}, 100);
	}

	onUpdateFocalArea(event: { index: number; focalArea: Partial<FocalArea>; isManualAspectRatioEdit?: boolean }): void {
		const actualIndex = this.resolveActualIndex(event.index);
		if (actualIndex === null) {
			return;
		}
		const updated = [...this.focalAreas];

		// If setting a new default, clear other defaults
		if (event.focalArea.isDefault) {
			updated.forEach((area, i) => {
				if (i !== actualIndex) {
					area.isDefault = false;
				}
			});
		}

		// Only mark aspect ratio as dirty if it's a manual user edit (from input field)
		if (event.isManualAspectRatioEdit && event.focalArea.aspectRatio !== undefined) {
			this.aspectRatioDirtyFlags.set(actualIndex, true);
		}

		updated[actualIndex] = { ...updated[actualIndex], ...event.focalArea };
		this.focalAreas = updated;
		this.focalAreas$.next([...this.focalAreas]);
		this.updateDirtyState();

		// If the bounding box was updated and this is the selected focal area, reload into cropper
		if (event.focalArea.boundingBox && actualIndex === this.selectedFocalAreaIndex) {
			this.loadFocalAreaIntoCropper(updated[actualIndex]);
		}
	}

	async onDeleteFocalArea(index: number): Promise<void> {
		const actualIndex = this.resolveActualIndex(index);
		if (actualIndex === null) {
			console.error('Could not resolve actual index for deletion:', index, 'visible indexes:', this.visibleFocalAreaIndexes);
			return;
		}

		// Verify the focal area exists before deleting
		if (!this.focalAreas[actualIndex]) {
			console.error('Focal area not found at actual index:', actualIndex);
			return;
		}

		const wasDefault = this.focalAreas[actualIndex]?.isDefault;
		const updated = this.focalAreas.filter((_, i) => i !== actualIndex);

		// If we deleted the default and there are still focal areas left, make the last one default
		if (wasDefault && updated.length > 0) {
			updated[updated.length - 1].isDefault = true;
		}

		this.focalAreas = updated;
		this.focalAreas$.next([...this.focalAreas]);
		this.updateDirtyState();

		if (this.selectedFocalAreaIndex === actualIndex) {
			this.selectedFocalAreaIndex = null;
			this.selectedIndex$.next(null);
		} else if (this.selectedFocalAreaIndex !== null && this.selectedFocalAreaIndex > actualIndex) {
			this.selectedFocalAreaIndex--;
			this.selectedIndex$.next(this.selectedFocalAreaIndex);
		}

		this.syncDisplayIndexes();

		// Persist the deletion to the API
		try {
			if (this.hasUnsavedChanges) {
				await this.onSave(false);
			}
		} catch (error) {
			console.error('Failed to persist focal area deletion:', error);
			this.globalService.triggerErrorMessage(undefined, 'Failed to delete focal area');
		}
	}

	onStartEditingName(index: number): void {
		const actualIndex = this.resolveActualIndex(index);
		if (actualIndex === null) {
			return;
		}
		this.editingNameIndex = actualIndex;
		this.syncDisplayIndexes();
	}

	onFinishEditingName(): void {
		this.editingNameIndex = null;
		this.syncDisplayIndexes();
	}

	onTagFilterSelectionChange(tags: string[]): void {
		// Check if tags actually changed
		const tagsChanged = JSON.stringify(this.selectedTagFilters) !== JSON.stringify(tags);


		this.selectedTagFilters = [...tags];
		this.rebuildVisibleFocalAreas();

		const selectionVisible = this.selectedFocalAreaIndex !== null &&
			this.visibleFocalAreaIndexes.includes(this.selectedFocalAreaIndex);

		if (!selectionVisible) {
			if (this.visibleFocalAreaIndexes.length > 0) {
				this.onSelectFocalArea(0);
			} else {
				this.selectedFocalAreaIndex = null;
				this.selectedIndex$.next(null);
				this.previewFocalArea = null;
			}
		}

		if (this.editingNameIndex !== null && !this.visibleFocalAreaIndexes.includes(this.editingNameIndex)) {
			this.editingNameIndex = null;
		}

		this.syncDisplayIndexes();

		// If auto-detect mode is enabled and tags actually changed, clear detection results and trigger re-detection
		if (this.autoDetectMode && this.previewMode !== 'original' && tagsChanged) {
			this.detectionResult = null;
			this.isLoadingDetection = true;
			// The preview component will automatically regenerate the URL when filterTags changes
			// via ngOnChanges, which will trigger the detection process
		}
	}

	async onAddSuggestion(suggestion: FocalArea): Promise<void> {
		// Clear preview when adding
		this.previewFocalArea = null;

		// Calculate aspect ratio from bounding box if not provided
		const aspectRatio = suggestion.aspectRatio ||
			FocalAreaUtils.calculateAspectRatio(suggestion.boundingBox.width, suggestion.boundingBox.height);

		// Add a single AI suggestion to focal areas
		// First focal area should be default, subsequent ones are non-default
		const newArea: FocalArea = {
			...suggestion,
			name: suggestion.name || `Focal Area ${this.focalAreas.length + 1}`,
			isDefault: this.focalAreas.length === 0, // First focal area is default
			aspectRatio: aspectRatio,
			aspectPriority: suggestion.aspectPriority || 'crop' // New focal areas use 'crop' priority
		};

		this.focalAreas = [...this.focalAreas, newArea];
		this.focalAreas$.next([...this.focalAreas]);
		this.updateDirtyState();

		// Optionally select the newly added focal area
		this.selectedFocalAreaIndex = this.focalAreas.length - 1;
		this.selectedIndex$.next(this.selectedFocalAreaIndex);

		// Load it into the cropper
		this.loadFocalAreaIntoCropper(newArea);

		try {
			if (this.hasUnsavedChanges) {
				await this.onSave(false);
			}
		} catch (error) {
			console.error('Auto-save failed after adding AI suggestion:', error);
		}
	}

	onPreviewSuggestion(suggestion: FocalArea): void {
		// Temporarily load the AI suggestion into the cropper for preview
		// without adding it to the focal areas list
		this.previewFocalArea = suggestion;
		this.selectedFocalAreaIndex = null; // Deselect any selected focal area
		this.selectedIndex$.next(null);
		this.loadFocalAreaIntoCropper(suggestion);
	}

	onCopyCropData(): void {
		if (this.selectedFocalArea) {
			const data = JSON.stringify(this.selectedFocalArea, null, 2);
			navigator.clipboard.writeText(data);
			alert('Focal area copied to clipboard');
		}
	}

	async onPasteCropData(): Promise<void> {
		try {
			const clipboardText = await navigator.clipboard.readText();
			const parsedData = JSON.parse(clipboardText) as FocalArea;

			// Validate required fields
			if (!parsedData.name || !parsedData.boundingBox || !parsedData.aspectRatio) {
				alert('Invalid focal area data: missing required fields (name, boundingBox, aspectRatio)');
				return;
			}

			// Validate bounding box structure
			const bbox = parsedData.boundingBox;
			if (typeof bbox.x !== 'number' || typeof bbox.y !== 'number' ||
				typeof bbox.width !== 'number' || typeof bbox.height !== 'number') {
				alert('Invalid bounding box: coordinates must be numbers');
				return;
			}

			// Create new focal area with incremented name
			const nameMatch = parsedData.name.match(/^(.*?)(\d+)$/);
			let newName: string;
			if (nameMatch) {
				const baseName = nameMatch[1];
				const num = parseInt(nameMatch[2], 10);
				newName = `${baseName}${num + 1}`;
			} else {
				newName = `${parsedData.name} Copy`;
			}

			const newFocalArea: FocalArea = {
				...parsedData,
				name: newName,
				isDefault: false // Never paste as default
			};

			this.focalAreas.push(newFocalArea);
			this.focalAreas$.next([...this.focalAreas]);
			this.updateDirtyState();
			alert(`Focal area pasted as "${newName}"`);
		} catch (error) {
			if (error instanceof SyntaxError) {
				alert('Invalid JSON in clipboard');
			} else {
				alert('Failed to paste focal area: ' + (error as Error).message);
			}
			console.error('Paste error:', error);
		}
	}

	async onLoadImageFromUrl(url: string): Promise<void> {
		if (!url || !url.trim()) {
			this.loadImageError = 'Please enter a valid URL';
			return;
		}

		this.isLoadingImage = true;
		this.loadImageError = null;

		try {
			// Get businessId from active project or business
				const project = this.projectsQuery.getActive();
				let businessId = this.coerceId(project?.business?.id) || this.coerceId(this.businessQuery.getActiveId());

			// Call backend to find asset by assetPath
		const findPayload: Record<string, any> = {
			assetPath: url.trim()
		};

		if (businessId && this.isUuid(businessId)) {
			findPayload.businessId = [businessId];
		}

			const response = await this.http.post<any>(`${environment.apiUrl}/asset/find`, findPayload).toPromise();


				if (response && response.data && response.data.length > 0) {
					// Case 1: Asset exists - load existing data (task 4.15)
					const existingAsset = response.data[0];
					this.applyAssetData(existingAsset, true);
				} else {
					// Case 2: Asset doesn't exist - create new partial asset (task 4.16)
					const newAsset = {
						assetPath: url.trim(),
						isRemote: true,
						metadata: {
							targetCrops: []
						}
					};

					this.asset = newAsset;
					this.assetId = undefined;
					this.imageUrl = url.trim();
					this.assetExistsInDb = false;
					this.setFocalAreasFromAsset(newAsset);

				}

				// Reset selection
				this.selectedFocalAreaIndex = null;
				this.selectedIndex$.next(null);
				this.assetExistsInDb = !!(response?.data?.length);

		} catch (error) {
			this.loadImageError = 'Failed to load image: ' + (error as Error).message;
			console.error('Load image error:', error);
		} finally {
			this.isLoadingImage = false;
		}
	}

	onReplaceImage(): void {
		// Check if there are existing focal areas
		if (this.focalAreas.length > 0) {
			const confirmed = confirm(
				'Replacing the image will clear all existing focal areas. Do you want to continue?'
			);
			if (!confirmed) {
				return;
			}
		}

		// Prompt for new URL
		const newUrl = prompt('Enter the new image URL:');
		if (newUrl) {
			this.onLoadImageFromUrl(newUrl);
		}
	}

	async onSave(showNotification: boolean = true): Promise<void> {
		if (!this.hasUnsavedChanges) {
			return;
		}

		this.isSaving = true;
		this.saveError = null;


		try {
		const siteId = this.coerceId(this.getActiveSiteId());
		if (!siteId) {
			throw new Error('Unable to determine site for asset save');
		}

			const url = (this.asset?.assetPath || this.imageUrl || '').trim();
			if (!url) {
				throw new Error('Unable to determine asset URL for save operation');
			}

		const metadata = {
			...(this.asset?.metadata || {}),
			targetCrops: this.focalAreas
		};

			const providerRaw = (this.asset?.provider || '').toString().toLowerCase();
			const provider: 'amazon' | 'external' = providerRaw === 'amazon' ? 'amazon' : 'external';

			const payload: Record<string, any> = {
				url,
				provider,
				metadata
			};

		const businessId = this.coerceId(this.getActiveBusinessId());
		if (businessId && this.isUuid(businessId)) {
			payload.businessId = businessId;
		}

		const projectId = this.coerceId(this.getActiveProjectId());
		if (projectId && this.isUuid(projectId)) {
			payload.projectId = projectId;
		}

			if (this.asset?.collectionIds?.length) {
				payload.collectionIds = this.asset.collectionIds;
			}

		if (this.assetExistsInDb && this.asset?.id && this.isUuid(this.asset.id)) {
			payload.assetId = this.asset.id;
		}

			const response = await this.http
				.post<any>(`${environment.apiUrl}/asset/site/${siteId}/from-url`, payload)
				.toPromise();

			if (response) {
				this.applyAssetData(response, true);
				this.assetUpdated.emit(response);
				if (showNotification) {
					this.globalService.triggerSaveMessage();
				}
			}
		} catch (error) {
			this.saveError = 'Failed to save focal areas: ' + (error as Error).message;
			console.error('Save error:', error);
		} finally {
			this.isSaving = false;
		}
	}

	onRetry(): void {
		this.onSave();
	}

	setPreviewMode(mode: string): void {
		// Don't do anything if no focal area is selected
		if (!this.selectedFocalArea) {
			return;
		}

		// Don't do anything if clicking the same mode
		if (this.previewMode === mode) {
			return;
		}

		this.previewMode = mode;

		// Clear detection results when switching preview modes in auto-detect mode
		if (this.autoDetectMode) {
			this.detectionResult = null;
			this.isLoadingDetection = true;
		}
	}

	/**
	 * Toggle auto-detection mode on/off
	 */
	toggleAutoDetect(): void {
		this.autoDetectMode = !this.autoDetectMode;

		// Clear detection results when toggling
		this.detectionResult = null;

		// Set loading state if turning on and in preview mode
		if (this.autoDetectMode && this.previewMode !== 'original') {
			this.isLoadingDetection = true;
		} else {
			this.isLoadingDetection = false;
		}
	}

	/**
	 * Handle detection result received from preview component
	 */
	onDetectionResult(result: DetectionResult): void {

		if (result) {
			// Store the detection result
			this.detectionResult = result;
			// Turn off loading state
			this.isLoadingDetection = false;
		}
	}

	onCustomAspectRatioChange(): void {
		// Validate the custom aspect ratio format
		const aspectRatioPattern = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/;
		if (!aspectRatioPattern.test(this.customAspectRatio.trim())) {
			alert('Invalid aspect ratio format. Please use format "X:Y" (e.g., "16:9")');
			this.customAspectRatio = '16:9';
		}
	}

	onCropperReady(): void {
		// Image dimensions will be available after cropper initializes
		// We'll get them from the ImageLoadedEvent when it's emitted
	}

	onImageLoaded(event: any): void {
		// Capture the natural dimensions of the loaded image
		this.imageNaturalWidth = event.original.size.width;
		this.imageNaturalHeight = event.original.size.height;

		// If initialTargetCrop is provided and we haven't matched/created yet, do it now
		if (this.initialTargetCrop && this.focalAreas.length === 0) {
			this.matchOrCreateFocalArea();
		}

		// If we have a selected focal area, load it into the cropper now that we have dimensions
		if (this.selectedFocalArea) {
			this.loadFocalAreaIntoCropper(this.selectedFocalArea);
		}
	}

	onHiddenImageLoad(event: Event): void {
		// Capture dimensions from hidden image when cropper is not visible
		const img = event.target as HTMLImageElement;
		if (img.naturalWidth && img.naturalHeight) {
			this.imageNaturalWidth = img.naturalWidth;
			this.imageNaturalHeight = img.naturalHeight;
		}
	}

	onImageCropped(event: ImageCroppedEvent): void {
		// Don't update if we're loading a focal area into the cropper (prevents duplicates)
		if (this.isLoadingFocalAreaIntoCropper) {
			return;
		}

		// Don't update if we're in preview mode (previewing an AI suggestion)
		if (this.previewFocalArea) {
			return;
		}

		if (this.selectedFocalAreaIndex === null || !this.imageNaturalWidth || !this.imageNaturalHeight) {
			return;
		}

		// Use imagePosition which is in the coordinate space of the original image
		// Convert pixel coordinates back to percentages
		const x = (event.imagePosition.x1 / this.imageNaturalWidth) * 100;
		const y = (event.imagePosition.y1 / this.imageNaturalHeight) * 100;
		const width = ((event.imagePosition.x2 - event.imagePosition.x1) / this.imageNaturalWidth) * 100;
		const height = ((event.imagePosition.y2 - event.imagePosition.y1) / this.imageNaturalHeight) * 100;

		// Calculate aspect ratio automatically unless user has manually edited it
		const isDirty = this.aspectRatioDirtyFlags.get(this.selectedFocalAreaIndex) || false;
		const updatedFields: Partial<FocalArea> = {
			boundingBox: { x, y, width, height }
		};

		// Auto-calculate aspect ratio if not manually edited
		if (!isDirty) {
			updatedFields.aspectRatio = FocalAreaUtils.calculateAspectRatio(width, height);
		}

		// Update the focal area with new bounding box coordinates
		this.onUpdateFocalArea({
			index: this.selectedFocalAreaIndex,
			focalArea: updatedFields
		});
	}

	private getActiveBusinessId(): string | number | undefined {
		return (
			this.asset?.businessId ||
			this.projectsQuery.getActive()?.business?.id ||
			this.businessQuery.getActiveId() ||
			undefined
		);
	}

	private getActiveProjectId(): string | number | undefined {
		return this.asset?.projectId || this.projectsQuery.getActive()?.id || undefined;
	}

	private getActiveSiteId(): string | undefined {
		return environment.organizationId || undefined;
	}

	private coerceId(value: unknown): string | undefined {
		if (typeof value === 'string') {
			return value;
		}
		if (typeof value === 'number') {
			return value.toString();
		}
		return undefined;
	}

	private isUuid(value: string): boolean {
		const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
		return uuidRegex.test(value);
	}

	/**
	 * Check if auto-generate button should be visible
	 */
	get shouldShowAutoGenerateButton(): boolean {
		return !!(this.creativeUnits && this.creativeUnits.length > 0);
	}

	/**
	 * Copy all focal areas to clipboard in JSON format
	 */
	async copyFocalAreas(): Promise<void> {
		try {
			const clipboardData = {
				version: '1.0',
				focalAreas: this.focalAreas
			};
			console.log('Copying focal areas to clipboard:', clipboardData);

			await navigator.clipboard.writeText(JSON.stringify(clipboardData, null, 2));

			this.globalService.triggerSuccessMessage('Focal areas copied to clipboard');
		} catch (error) {
			console.error('Failed to copy focal areas:', error);
			this.globalService.triggerErrorMessage(null, 'Failed to copy focal areas to clipboard');
		}
	}

	/**
	 * Paste focal areas from clipboard
	 */
	async pasteFocalAreas(): Promise<void> {
		try {
			// Read from clipboard
			const clipboardText = await navigator.clipboard.readText();

			if (!clipboardText || !clipboardText.trim()) {
				this.globalService.triggerErrorMessage(null, 'No focal area data found in clipboard');
				return;
			}

			// Parse JSON
			let clipboardData: any;
			try {
				clipboardData = JSON.parse(clipboardText);
			} catch (parseError) {
				this.globalService.triggerErrorMessage(null, 'Invalid focal area data in clipboard');
				return;
			}

			// Validate structure
			if (!clipboardData.focalAreas || !Array.isArray(clipboardData.focalAreas)) {
				this.globalService.triggerErrorMessage(null, 'Invalid focal area data in clipboard');
				return;
			}

			// Ask user for paste mode: replace or append
			const mode = await this.showPasteModeDialog();
			if (!mode) {
				return; // User cancelled
			}

			// Create snapshot for undo
			this.snapshotBeforeOperation = this.cloneFocalAreas(this.focalAreas);

			// Apply the paste operation
			if (mode === 'replace') {
				this.focalAreas = this.cloneFocalAreas(clipboardData.focalAreas);
			} else if (mode === 'append') {
				this.focalAreas = [...this.focalAreas, ...this.cloneFocalAreas(clipboardData.focalAreas)];
			}

			this.focalAreas$.next([...this.focalAreas]);
			this.rebuildVisibleFocalAreas();
			this.updateDirtyState();

			// Auto-save the pasted focal areas
			await this.onSave(false);

			// Show success message with undo option
			const message = mode === 'replace'
				? 'Focal areas replaced successfully'
				: `${clipboardData.focalAreas.length} focal areas added`;

			this.globalService.triggerSuccessMessage(message, { duration: 5000 }, {
				label: 'Undo',
				callback: () => this.undoOperation()
			});

		} catch (error) {
			console.error('Failed to paste focal areas:', error);
			this.globalService.triggerErrorMessage(null, 'Failed to paste focal areas');
		}
	}

	/**
	 * Show dialog to choose paste mode (replace or append)
	 */
	private async showPasteModeDialog(): Promise<'replace' | 'append' | null> {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: <ConfirmDialogData>{
				title: 'Paste Focal Areas',
				message: `You have ${this.focalAreas.length} existing focal area${this.focalAreas.length !== 1 ? 's' : ''}. How would you like to paste?`,
				confirm: 'Replace All',
				secondaryAction: 'Add to Existing',
				secondaryActionValue: 'append',
				cancel: 'Cancel'
			}
		});

		const result = await firstValueFrom(dialogRef.afterClosed());

		if (result === 'append') {
			return 'append';
		} else if (result) {
			// Confirmed (returns full data object)
			return 'replace';
		}

		// Cancelled
		return null;
	}

	/**
	 * Show dialog to choose generate mode (replace or append)
	 */
	private async showGenerateModeDialog(): Promise<'replace' | 'append' | null> {
		if (this.focalAreas.length === 0) {
			// No existing focal areas, just generate
			return 'replace';
		}

		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: <ConfirmDialogData>{
				title: 'Generate Focal Areas',
				message: `You have ${this.focalAreas.length} existing focal area${this.focalAreas.length !== 1 ? 's' : ''}. How would you like to generate?`,
				confirm: 'Replace All',
				secondaryAction: 'Add to Existing',
				secondaryActionValue: 'append',
				cancel: 'Cancel'
			}
		});

		const result = await firstValueFrom(dialogRef.afterClosed());

		if (result === 'append') {
			return 'append';
		} else if (result) {
			// Confirmed (returns full data object)
			return 'replace';
		}

		// Cancelled
		return null;
	}

	/**
	 * Auto-generate focal areas from creative units
	 */
	async autoGenerateFocalAreas(): Promise<void> {
		try {

			if (!this.creativeUnits || this.creativeUnits.length === 0) {
				this.globalService.triggerErrorMessage(null, 'No creative units available for generation');
				return;
			}

			// Show generation mode dialog
			const mode = await this.showGenerateModeDialog();

			if (!mode) {
				return;
			}


			// Create snapshot for undo
			this.snapshotBeforeOperation = this.cloneFocalAreas(this.focalAreas);

			// Extract unique aspect ratios
			const uniqueRatios = this.extractUniqueAspectRatios();

			if (uniqueRatios.length === 0) {
				this.globalService.triggerErrorMessage(null, 'No valid aspect ratios found in creative units');
				return;
			}

			// Generate focal areas
			const newFocalAreas: FocalArea[] = [];

			for (const ratioInfo of uniqueRatios) {
				const focalArea = this.calculateFocalAreaDimensions(ratioInfo);
				if (focalArea) {
					newFocalAreas.push(focalArea);
				}
			}


			if (newFocalAreas.length === 0) {
				this.globalService.triggerErrorMessage(null, 'Could not generate focal areas (image dimensions may not be available)');
				return;
			}

			// Apply the generation based on mode
			if (mode === 'replace') {
				this.focalAreas = newFocalAreas;
			} else if (mode === 'append') {
				this.focalAreas = [...this.focalAreas, ...newFocalAreas];
			}

			this.focalAreas$.next([...this.focalAreas]);
			this.rebuildVisibleFocalAreas();
			this.updateDirtyState();
			this.cdr.detectChanges();

			// Auto-save the generated focal areas
			await this.onSave(false);

			// Show success message with undo option
			const message = mode === 'replace'
				? 'Focal areas auto-generated successfully'
				: `${newFocalAreas.length} focal areas added`;

			this.globalService.triggerSuccessMessage(message, { duration: 5000 }, {
				label: 'Undo',
				callback: () => this.undoOperation()
			});

		} catch (error) {
			this.globalService.triggerErrorMessage(null, 'Error generating focal areas: ' + error.message);
		}
	}

	/**
	 * Extract unique aspect ratios from creative units
	 */
	private extractUniqueAspectRatios(): Array<{ ratio: number; width: number; height: number; name: string }> {

		if (!this.creativeUnits) {
			return [];
		}

		const uniqueRatiosMap = new Map<string, { ratio: number; width: number; height: number; name: string }>();

		for (const unit of this.creativeUnits) {
			const ratio = unit.width / unit.height;
			const key = ratio.toFixed(4); // Round to avoid floating-point issues

			if (!uniqueRatiosMap.has(key)) {
				uniqueRatiosMap.set(key, {
					ratio,
					width: unit.width,
					height: unit.height,
					name: unit.name
				});
			}
		}

		const result = Array.from(uniqueRatiosMap.values());
		return result;
	}

	/**
	 * Calculate focal area dimensions for a given aspect ratio
	 */
	private calculateFocalAreaDimensions(ratioInfo: { ratio: number; width: number; height: number; name: string }): FocalArea | null {

		if (!this.imageNaturalWidth || !this.imageNaturalHeight) {
			return null;
		}

		const imageAspect = this.imageNaturalWidth / this.imageNaturalHeight;
		const unitAspect = ratioInfo.ratio;

		let cropWidth: number;
		let cropHeight: number;

		if (unitAspect > imageAspect) {
			// Unit is wider than image - fit to image width
			cropWidth = 100; // 100% of image width
			cropHeight = (100 / unitAspect) * imageAspect;
		} else {
			// Unit is taller than image - fit to image height
			cropHeight = 100; // 100% of image height
			cropWidth = (100 * unitAspect) / imageAspect;
		}

		// Center the crop
		const x = (100 - cropWidth) / 2;
		const y = (100 - cropHeight) / 2;

		// Create focal area name with aspect ratio instead of pixel dimensions
		const aspectRatioStr = FocalAreaUtils.calculateAspectRatio(cropWidth, cropHeight);
		const unitAspectRatioStr = FocalAreaUtils.calculateAspectRatio(ratioInfo.width, ratioInfo.height);
		const name = `${ratioInfo.name} (${unitAspectRatioStr})`;

		const result = {
			...DEFAULT_FOCAL_AREA,
			name: name,
			boundingBox: {
				x: x,
				y: y,
				width: cropWidth,
				height: cropHeight
			},
			aspectRatio: aspectRatioStr,
			tags: ['auto-generated'],
			isDefault: false
		};
		return result;
	}

	/**
	 * Undo the last paste or auto-generate operation
	 */
	private async undoOperation(): Promise<void> {
		if (!this.snapshotBeforeOperation) {
			return;
		}

		this.focalAreas = this.cloneFocalAreas(this.snapshotBeforeOperation);
		this.focalAreas$.next([...this.focalAreas]);
		this.rebuildVisibleFocalAreas();
		this.updateDirtyState();
		this.snapshotBeforeOperation = null;

		// Auto-save after undo
		await this.onSave(false);
	}

	/**
	 * Optimize image URL using Tasker to limit resolution for better performance
	 * Limits images to 1200px max width while maintaining aspect ratio
	 */
	getOptimizedImageUrl(assetPath: string): string {
		if (!assetPath) {
			return assetPath;
		}

		const optimizations: Partial<LayerOptimizationsDto> = {
			provider: 'tasker',
			resize: {
				mode: ResizeMode.LIMIT,
				width: '1200',
				height: '1200',
				gravity: undefined,
				x: null,
				y: null,
				zoom: null,
				zoomGravity: null
			}
		};

		const url = LayerOptimizationsDto.optimizeAsset(
			assetPath,
			LayerType.IMAGE,
			optimizations as LayerOptimizationsDto,
			environment.cloudinaryCloudName,
			environment.taskerEngineUrl,
			environment.apiUrl
		);
		
		return url;
	}
}
